import api from "../http/api";

export const financePredictions = async ({ institutionId, pageNumber = 1, pageSize = 15, searchTerm, courseName }) => {
  try {
    const params = new URLSearchParams();
    params.append('pageNumber', pageNumber);
    params.append('pageSize', pageSize);
    if (searchTerm) {
      const parsed = parseInt(searchTerm, 10);
      if (!isNaN(parsed)) params.append('studentId', parsed);
    }
    if (courseName) params.append('courseName', courseName);

    const response = await api.client.get(
      `api/v1/FinancePredictions?${params.toString()}`,
      {
        headers: {
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar previsões financeiras:", error);
    throw new Error("Falha na conexão com a API de previsões financeiras");
  }
};

export const predictionHistory = async ({ institutionId, pageNumber = 1, pageSize = 10 }) => {
  try {
    const response = await api.client.get(
      `api/v1/PredictionHistory?institutionId=${institutionId}&pageNumber=${pageNumber}&pageSize=${pageSize}`
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar histórico de predições:", error);
    throw new Error("Falha na conexão com a API de histórico de predições");
  }
};

export const predictiveNextRun = async () => {
  try {
    const response = await api.client.get('api/v1/PredictiveAnalysis/next-run');
    return response.data;
  } catch (error) {
    // informação opcional: sem próxima execução disponível, a tela apenas omite a linha
    console.error("Erro ao buscar próxima execução da análise preditiva:", error);
    return null;
  }
};

export const studentFeaturesTraining = async ({ institutionId, pageNumber = 1, pageSize = 10 }) => {
  try {
    const response = await api.client.get(
      `api/v1/StudentFeaturesTraining?pageNumber=${pageNumber}&pageSize=${pageSize}`,
      {
        headers: {
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar dados de treinamento de alunos:", error);
    throw new Error("Falha na conexão com a API de dados de treinamento");
  }
};

export const defaultingStudents = async ({ 
  institutionId, 
  startDate, 
  endDate,
  knowledgeArea,
  discipline,
  courseTypeId,
  localityId
}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (startDate) {
      queryParams.append('startDate', startDate);
    }
    
    if (endDate) {
      queryParams.append('endDate', endDate);
    }

    if (knowledgeArea) {
      queryParams.append('knowledgeArea', knowledgeArea);
    }

    if (discipline) {
      queryParams.append('discipline', discipline);
    }

    if (courseTypeId) {
      queryParams.append('courseTypeId', courseTypeId);
    }

    if (localityId) {
      queryParams.append('localityId', localityId);
    }

    const response = await api.client.get(
      `api/v1/DefaultingStudents?${queryParams.toString()}`,
      {
        headers: {
          'Content-Type': 'application/json',
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar alunos inadimplentes:", error);
    throw new Error("Falha na conexão com a API de alunos inadimplentes");
  }
};

export const monthlyDelinquency = async ({ institutionId, forceRefresh = false }) => {
  try {
    const url = forceRefresh
      ? 'api/v1/MonthlyDelinquency?refresh=true'
      : 'api/v1/MonthlyDelinquency';

    const response = await api.client.get(
      url,
      {
        headers: {
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar inadimplência mensal:", error);
    throw new Error("Falha na conexão com a API de inadimplência mensal");
  }
};

export const delinquencyTrend = async ({ institutionId, forceRefresh = false }) => {
  try {
    const url = forceRefresh
      ? 'api/v1/DelinquencyTrend?refresh=true'
      : 'api/v1/DelinquencyTrend';

    const response = await api.client.get(
      url,
      {
        headers: {
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar tendência de inadimplência:", error);
    throw new Error("Falha na conexão com a API de tendência de inadimplência");
  }
};

export const dropoutRiskStudents = async ({ institutionId, startDate, endDate, pageNumber = 1, pageSize = 100 }) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    params.append('pageNumber', pageNumber);
    params.append('pageSize', pageSize);

    const response = await api.client.get(
      `api/v1/DropoutRiskStudents?${params.toString()}`,
      {
        headers: {
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar alunos com risco de evasão:", error);
    throw new Error("Falha na conexão com a API de risco de evasão");
  }
};

export const dropoutRiskResults = async ({ institutionId, pageNumber = 1, pageSize = 15 }) => {
  try {
    const params = new URLSearchParams();
    params.append('pageNumber', pageNumber);
    params.append('pageSize', pageSize);

    const response = await api.client.get(
      `api/v1/DropoutRiskStudents/results?${params.toString()}`,
      {
        headers: {
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar resultados de risco de evasão:", error);
    throw new Error("Falha na conexão com a API de resultados de evasão");
  }
};

export const dropoutRiskDashboard = async ({ institutionId }) => {
  try {
    const response = await api.client.get(
      'api/v1/DropoutRiskStudents/dashboard',
      {
        headers: {
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar dashboard de evasão:", error);
    throw new Error("Falha na conexão com a API de dashboard de evasão");
  }
};

export const dropoutRiskHistory = async ({ institutionId, pageNumber = 1, pageSize = 10 }) => {
  try {
    const params = new URLSearchParams();
    params.append('pageNumber', pageNumber);
    params.append('pageSize', pageSize);

    const response = await api.client.get(
      `api/v1/DropoutRiskStudents/history?${params.toString()}`,
      {
        headers: {
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar histórico de evasão:", error);
    throw new Error("Falha na conexão com a API de histórico de evasão");
  }
};

export const predictiveFinanceInstitution = async ({ institutionId, startDate, endDate }) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (startDate) {
      queryParams.append('startDate', startDate);
    }
    
    if (endDate) {
      queryParams.append('endDate', endDate);
    }

    const response = await api.client.get(
      `api/v1/PredictiveFinanceInstitution?${queryParams.toString()}`,
      {
        headers: {
          'Content-Type': 'application/json',
          institutionId: institutionId
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar dados de inadimplência preditiva:", error);
    throw new Error("Falha na conexão com a API de inadimplência preditiva");
  }
};
